import { FaRegCheckCircle } from "react-icons/fa";
import { FiChevronLeft } from "react-icons/fi";
import styled from "styled-components";

import FlexBox from "../FlexBox";
import { Body2, H2 } from "../Headings";
import {
  BRICK_TERRACOTA,
  DAVYS_GREY,
  DAVYS_GREY_800,
  ERROR_RED,
  SUCCESS_GREEN,
} from "../colors";

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled(FlexBox)`
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1rem;
  border-bottom: 1px solid ${DAVYS_GREY[300]};
`;

const BackButton = styled.button`
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${DAVYS_GREY[800]};
  cursor: pointer;
  padding: 0;
`;

const Spacer = styled.div`
  width: 4rem;
`;

const Content = styled(FlexBox)`
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const Section = styled(FlexBox)`
  flex-direction: column;
  gap: 1rem;
`;

const ExampleBox = styled.div`
  border-radius: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: ${({ variant }) =>
    variant === "good"
      ? (SUCCESS_GREEN[50] ?? "#f0faf4")
      : (BRICK_TERRACOTA[50] ?? "#fff5f2")};
  border: 1px solid
    ${({ variant }) =>
      variant === "good"
        ? (SUCCESS_GREEN[200] ?? "#b3dfc4")
        : (BRICK_TERRACOTA[200] ?? "#ffe9e3")};
`;

const BulletItem = styled(FlexBox)`
  align-items: flex-start;
  gap: 0.5rem;
`;

const Bullet = styled.span`
  flex-shrink: 0;
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: ${DAVYS_GREY[700]};
  margin-top: 0.45rem;
`;

const TipRow = styled(FlexBox)`
  align-items: flex-start;
  gap: 0.5rem;
`;

const Divider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${DAVYS_GREY[300]};
  width: 100%;
`;

const EndDivider = styled(FlexBox)`
  align-items: center;
  width: 100%;
  column-gap: 0.5rem;
`;

const DividerLine = styled.span`
  flex: 1;
  height: 1px;
  background: ${DAVYS_GREY[300]};
`;

const DividerDots = styled(FlexBox)`
  align-items: center;
  column-gap: 0.35rem;
`;

const Dot = styled.span`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: ${DAVYS_GREY[600]};
`;

const PROMPT_EXAMPLES = [
  `"Summarise this client's mental health journey so far."`,
  `"Highlight key concerns from the last 3 sessions."`,
  `"List any risk factors that stand out in this case."`,
  `"What symptoms or themes have repeated over time?"`,
  `"Suggest next steps based on recent session notes."`,
];

const USAGE_TIPS = [
  "Tap a prompt to insert it instantly",
  "Edit it before submitting to suit your case",
  "Use multiple prompts over time to explore different angles",
];

const WRITING_TIPS = [
  {
    label: "Be Specific",
    description: null,
    good: `"Summarise concerns about sleep in the last 5 sessions."`,
    avoid: `"Tell me about the client."`,
  },
  {
    label: "Add Context",
    description:
      "Mention anything you want the AI to focus on - like mood, medication, or history.",
  },
  {
    label: "Ask One Clear Question at a Time",
    description: "Keep prompts focused for more targeted answers.",
  },
  {
    label: "Use Follow-ups",
    description:
      "You can ask the AI to go deeper or reframe based on the response you get.",
  },
  {
    label: "Rephrase if needed",
    description:
      "If a prompt doesn't give the expected result, tweak it slightly and try again.",
  },
];

const AiChatHelpCentreView = ({ onBack }) => {
  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <FiChevronLeft size={20} />
          <Body2>Back</Body2>
        </BackButton>

        <H2 bold>Help</H2>

        <Spacer />
      </Header>

      <Content>
        <Section>
          <H2 bold>Prompt Suggestions: Quick Ways to Get Started</H2>

          <Body2 lineHeight="1.4rem">
            Not sure what to ask? The AI tool can respond to specific prompts
            you enter based on your client&apos;s data. If you&apos;re unsure
            what to ask, we&apos;ve added ready-to-use suggestions to help you
            begin with ease.
          </Body2>

          <FlexBox column rowGap="0.5rem">
            <Body2 bold>You can use prompts like:</Body2>

            <FlexBox column rowGap="0.25rem">
              {PROMPT_EXAMPLES.map(example => (
                <BulletItem key={example}>
                  <Bullet />
                  <Body2>{example}</Body2>
                </BulletItem>
              ))}
            </FlexBox>
          </FlexBox>

          <FlexBox column rowGap="0.5rem">
            <FlexBox columnGap="0.25rem" align="center">
              <Body2 bold>These suggestions are just a starting point.</Body2>
              <Body2>You can:</Body2>
            </FlexBox>

            <FlexBox column rowGap="0.25rem">
              {USAGE_TIPS.map(tip => (
                <BulletItem key={tip}>
                  <Bullet />
                  <Body2>{tip}</Body2>
                </BulletItem>
              ))}
            </FlexBox>
          </FlexBox>
        </Section>
        <Divider />
        <Section>
          <H2 bold>Prompt Writing Tips: How to Get Better Results</H2>

          <Body2 lineHeight="1.4rem">
            The more specific and focused your prompt, the more accurate and
            helpful the AI response will be. Here are some simple ways to get
            the best outcomes from your questions.
          </Body2>

          <FlexBox column rowGap="1rem">
            {WRITING_TIPS.map(tip => (
              <FlexBox key={tip.label} column rowGap="0.5rem">
                <TipRow>
                  <FaRegCheckCircle
                    size={18}
                    color={SUCCESS_GREEN[700]}
                    style={{ flexShrink: 0, marginTop: "0.1rem" }}
                  />
                  <Body2 bold>{tip.label}</Body2>
                </TipRow>

                {tip.description && (
                  <Body2 lineHeight="1.4rem">{tip.description}</Body2>
                )}

                {tip.good && (
                  <FlexBox column rowGap="0.5rem">
                    <ExampleBox variant="good">
                      <Body2 color={SUCCESS_GREEN[800]}>
                        Good:
                        <Body2 color={DAVYS_GREY_800}> {tip.good}</Body2>
                      </Body2>
                    </ExampleBox>
                    <ExampleBox variant="avoid">
                      <Body2 color={ERROR_RED[800]}>
                        Avoid:
                        <Body2 color={DAVYS_GREY_800}> {tip.avoid}</Body2>
                      </Body2>
                    </ExampleBox>
                  </FlexBox>
                )}
              </FlexBox>
            ))}
          </FlexBox>
        </Section>
        <EndDivider>
          <DividerLine />
          <DividerDots>
            <Dot />
            <Dot />
            <Dot />
          </DividerDots>
          <DividerLine />
        </EndDivider>
      </Content>
    </Container>
  );
};

export default AiChatHelpCentreView;
